import adminApiClient from "@/lib/api/admin-api-client";
import type { AccessRole } from "@/lib/api/portal-access-role";

export type PortalUserStatus = "pending" | "active" | "verified";

export type AdminPortalUser = {
  id: string;
  contact_name: string;
  email: string;
  company_name: string;
  status: PortalUserStatus;
  access_role: AccessRole | null;
  created_at: string;
  access_request_count: number;
};

export type AdminPortalUserSummary = {
  total: number;
  pending: number;
  active: number;
  verified: number;
};

export const EMPTY_PORTAL_USER_SUMMARY: AdminPortalUserSummary = {
  total: 0,
  pending: 0,
  active: 0,
  verified: 0,
};

export type AdminListPortalUsersResponse = {
  users: AdminPortalUser[];
  total: number;
  summary: AdminPortalUserSummary;
};

export type AdminListPortalUsersParams = {
  q?: string;
  status?: PortalUserStatus;
  access_role?: AccessRole;
  page?: number;
  limit?: number;
  sort?: string;
  order?: "asc" | "desc";
};

const ADMIN_PORTAL_USERS_PATH = "/api/v1/admin/portal-users";

export async function listAdminPortalUsers(
  params: AdminListPortalUsersParams = {},
): Promise<AdminListPortalUsersResponse> {
  const { data } = await adminApiClient.get<AdminListPortalUsersResponse>(
    ADMIN_PORTAL_USERS_PATH,
    {
      params: {
        ...(params.q?.trim() ? { q: params.q.trim() } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.access_role ? { access_role: params.access_role } : {}),
        ...(params.page ? { page: params.page } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.sort ? { sort: params.sort } : {}),
        ...(params.order ? { order: params.order } : {}),
      },
    },
  );
  return data;
}

export function portalUserStatusLabel(status: PortalUserStatus): string {
  if (status === "verified") return "Verified";
  if (status === "active") return "Active";
  return "Pending";
}

export function formatPortalUserDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export async function updateAdminPortalUserAccessRole(
  id: string,
  accessRole: AccessRole,
): Promise<AdminPortalUser> {
  const { data } = await adminApiClient.patch<AdminPortalUser>(
    `${ADMIN_PORTAL_USERS_PATH}/${id}`,
    { access_role: accessRole },
  );
  return data;
}
